import type { NormalizedCacheObject } from '@apollo/client';

const DATABASE_NAME = 'travelsment-itinerary-viewer';
const DATABASE_VERSION = 1;
const STORE_NAME = 'apollo-cache';
const TARGET_DB_SIZE_BYTES = 10 * 1024 * 1024;

const requestPersistentStorage = async (): Promise<void> => {
  if (!('storage' in navigator)) return;

  try {
    if ('estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      if ((estimate.quota ?? 0) < TARGET_DB_SIZE_BYTES) {
        // IndexedDB quota is browser-controlled, so the app requests persistence
        // and keeps the target cache size around 10 MB where available.
      }
    }

    if ('persist' in navigator.storage) {
      await navigator.storage.persist();
    }
  } catch {
    // Ignore quota/persistence failures; the app can still use IndexedDB with browser defaults.
  }
};

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      database.createObjectStore(STORE_NAME);
    }
  };

  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

requestPersistentStorage();

export const readApolloCache = async (key: string): Promise<NormalizedCacheObject | null> => {
  if (!('indexedDB' in window)) return null;

  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly')
      .objectStore(STORE_NAME)
      .get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

export const writeApolloCache = async (key: string, value: NormalizedCacheObject): Promise<void> => {
  if (!('indexedDB' in window)) return;

  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readwrite')
      .objectStore(STORE_NAME)
      .put(value, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};